import html2canvas from 'html2canvas';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';
import {
  MEMORY_OPTIMIZED_OPTIONS,
  cleanupCanvas,
  IMAGE_FORMAT,
  IMAGE_QUALITY,
} from './snapshot.utils';
import { cloneElement, sanitizeElementTree, cleanupClonedElement } from './sanitize-dom';
import type { Options } from 'html2canvas';

const prepareSanitizedElement = (element: HTMLElement): HTMLElement => {
  const clone = cloneElement(element);
  clone.style.position = 'absolute';
  clone.style.left = '-9999px';
  clone.style.top = '-9999px';
  document.body.appendChild(clone);
  sanitizeElementTree(clone);
  return clone;
};

const cleanupSanitizedNodes = (nodes: HTMLElement[]) => {
  nodes.forEach(cleanupClonedElement);
};

const canvasToBlobAsync = (canvas: HTMLCanvasElement, type: string, quality: number) => {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          const error = new Error('Failed to create blob from canvas');

          reportError({
            metadata: {
              name: 'Canvas Blob Creation Error',
              message: `${error.message} | Canvas: ${canvas.width}x${canvas.height} | Format: ${type} | Quality: ${quality}`,
            },
            severity: ObservabilityErrorSeverity.ERROR,
            groupingHash: 'canvas-blob-creation-error',
          });

          reject(error);
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });
};

export const takeSnapshot = async (element: HTMLElement = document.body) => {
  let canvas: HTMLCanvasElement | null = null;
  let sanitizedElement: HTMLElement | null = null;

  try {
    sanitizedElement = prepareSanitizedElement(element);
    canvas = await html2canvas(sanitizedElement, MEMORY_OPTIMIZED_OPTIONS);

    const blob = await canvasToBlobAsync(canvas, IMAGE_FORMAT, IMAGE_QUALITY);

    cleanupCanvas(canvas);
    cleanupClonedElement(sanitizedElement);

    return blob;
  } catch (error) {
    if (canvas) {
      cleanupCanvas(canvas);
    }

    if (sanitizedElement) {
      cleanupClonedElement(sanitizedElement);
    }

    reportError({
      metadata: {
        name: 'Snapshot Error',
        message: `${error instanceof Error ? error.message : String(error)} | Canvas: ${canvas?.width || 0}x${canvas?.height || 0}`,
      },
      severity: ObservabilityErrorSeverity.ERROR,
      groupingHash: 'Snapshot-error',
    });

    throw error;
  }
};

interface Html2CanvasOptions extends Partial<Options> {
  type?: string; // Image format, e.g., 'image/png'
  quality?: number; // Image quality (0 to 1 for JPEG/WEBP)
}

interface TakeFullPageSnapshotProps {
  nodes: HTMLElement[];
  options?: Html2CanvasOptions;
  cbOnComplete?: (image: Blob) => void;
}

export const takeFullPageSnapshot = async ({
  nodes,
  options = {},
  cbOnComplete,
}: TakeFullPageSnapshotProps) => {
  let combinedCanvas: HTMLCanvasElement | null = null;
  const processedCanvases: HTMLCanvasElement[] = [];
  const sanitizedNodes: HTMLElement[] = [];

  try {
    if (!nodes || nodes.length === 0) {
      throw new Error('You should provide an array of valid HTML nodes.');
    }

    const mergedOptions = { ...MEMORY_OPTIMIZED_OPTIONS, ...options };

    for (const node of nodes.filter(Boolean)) {
      try {
        sanitizedNodes.push(prepareSanitizedElement(node));
      } catch (error) {
        reportError({
          metadata: {
            name: 'Node Sanitization Error',
            message: `Failed to sanitize node: ${error instanceof Error ? error.message : String(error)}`,
          },
          severity: ObservabilityErrorSeverity.ERROR,
          groupingHash: 'node-sanitization-error',
        });
      }
    }

    // TODO: Check if it's possible to process the nodes directly into a combined canvas instead of creating a canvas for each node
    // Process nodes sequentially to prevent memory spikes
    for (const sanitizedNode of sanitizedNodes) {
      try {
        const canvas = await html2canvas(sanitizedNode, mergedOptions);
        processedCanvases.push(canvas);
      } catch (error) {
        reportError({
          metadata: {
            name: 'Node Canvas Creation Error',
            message: `Failed to create canvas for node: ${error instanceof Error ? error.message : String(error)}`,
          },
          severity: ObservabilityErrorSeverity.ERROR,
          groupingHash: 'node-canvas-creation-error',
        });
      }
    }

    if (processedCanvases.length === 0) {
      throw new Error('No nodes were successfully processed into canvases.');
    }

    // Calculate combined dimensions with 5px padding
    const totalHeight = processedCanvases.reduce((sum, canvas) => sum + canvas.height + 5, -5);
    const maxWidth = Math.max(...processedCanvases.map((canvas) => canvas.width));

    // Create combined canvas
    combinedCanvas = document.createElement('canvas');
    combinedCanvas.width = maxWidth;
    combinedCanvas.height = totalHeight;
    const combinedContext = combinedCanvas.getContext('2d');

    if (!combinedContext) {
      throw new Error('Failed to get 2D context');
    }

    // Draw each canvas sequentially and clean up immediately
    let currentHeight = 0;
    processedCanvases.forEach((canvas) => {
      combinedContext.drawImage(canvas, 0, currentHeight);
      currentHeight += canvas.height + 5;

      cleanupCanvas(canvas);
    });

    cleanupSanitizedNodes(sanitizedNodes);

    return new Promise<Blob>((resolve, reject) => {
      combinedCanvas!.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from combined canvas'));
            return;
          }

          if (cbOnComplete) {
            cbOnComplete(blob);
          }

          if (combinedCanvas) {
            cleanupCanvas(combinedCanvas);
          }

          resolve(blob);
        },
        mergedOptions.type || IMAGE_FORMAT,
        mergedOptions.quality || IMAGE_QUALITY
      );
    });
  } catch (err) {
    processedCanvases.forEach(cleanupCanvas);
    if (combinedCanvas) {
      cleanupCanvas(combinedCanvas);
    }

    cleanupSanitizedNodes(sanitizedNodes);

    reportError({
      metadata: {
        name: 'Full Page Snapshot Error',
        message: `${err instanceof Error ? err.message : String(err)} | Nodes: ${nodes?.length || 0} | Processed: ${processedCanvases?.length || 0} | Canvas: ${combinedCanvas?.width || 0}x${combinedCanvas?.height || 0}`,
      },
      severity: ObservabilityErrorSeverity.ERROR,
      groupingHash: 'full-page-snapshot-error',
    });
    return null;
  }
};
